'use client';

/**
 * ProductFacetsCard — the facet REFINE lens, as a collection card.
 *
 * Maps the payload's facets → one FacetCard each (uniform envelope + body by
 * shape), with the applied state pinned on top as an ActiveFiltersCard. The
 * rail is a COMPOSITION of facet cards, not a bespoke filter widget — same
 * doctrine as the canvas composing product cards into shelves. Quiet chrome
 * (off-white, hairline dividers) marks these as composition units, lighter than
 * the objects-you-act-on on the canvas.
 *
 * Surface-agnostic and relocatable: neutral container (NOT RailCard). Reads
 * {query, filters} from the product-search store, fetches product_search for the
 * facet payload (same params as the results grid → React Query dedups), toggles
 * values into the store. Derives nothing.
 *
 * @see feedback_cards-surface-agnostic-relocatable
 * @see klyde_edge/ui_config/composition/cards/vendors.py — content_key="product_facets"
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useMemo, useState } from 'react';
import { Loader2, SlidersHorizontal } from 'lucide-react';
import { useStrategyData } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useProductSearchStore } from '@/lib/stores/product-search-store';
import { useProductLabel } from '@/lib/stores/total-matrix-store';
import { useCardExport, CardPlacementProvider } from '@/lib/design/card-export';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { FacetCard, type Facet } from './facet-card';
import { ActiveFiltersCard, type ActiveChip } from './active-filters-card';

interface ProductSearchResponse {
  total: number;
  returned: number;
  capped: boolean;
  facets: Facet[];
}

export function ProductFacetsCard() {
  const query = useProductSearchStore((s) => s.query);
  const filters = useProductSearchStore((s) => s.filters);
  const setFilter = useProductSearchStore((s) => s.setFilter);
  const strategyName = useCapabilityStore((s) => s.strategyName('product_intelligence', 'search'));

  const labelTitle = useProductLabel('lens_filter');
  const labelEmpty = useProductLabel('filter_empty');
  const labelLoading = useProductLabel('filter_loading');
  const labelTail = useProductLabel('filter_tail');
  const moreLabel = useProductLabel('filter_more');
  const clearAllLabel = useProductLabel('filter_clear_all');

  // Same params as the canvas results card → React Query dedups the fetch.
  const params = useMemo(() => ({ query, filters, limit: 150 }), [query, filters]);
  const enabled =
    !!strategyName && (query.trim().length > 0 || Object.keys(filters).length > 0);
  const { data, isLoading, error } = useStrategyData<ProductSearchResponse>(
    strategyName,
    params,
    enabled,
  );
  const res = data?.data;
  const facets = res?.facets ?? [];
  // Vendor leads the stack; the rest follow catalogue order.
  const spineAll = facets.filter((f) => f.tier === 'spine');
  const spine = [
    ...spineAll.filter((f) => f.key === 'vendor'),
    ...spineAll.filter((f) => f.key !== 'vendor'),
  ];
  const tail = facets.filter((f) => f.tier === 'tail');

  // Accordion: all collapsed except Vendor, until the user touches it.
  const [openKeys, setOpenKeys] = useState<Set<string> | null>(null);
  const isOpen = (f: Facet) => (openKeys === null ? f.key === 'vendor' : openKeys.has(f.key));
  const toggleOpen = (f: Facet) =>
    setOpenKeys((prev) => {
      const base = new Set(prev ?? ['vendor']);
      base.has(f.key) ? base.delete(f.key) : base.add(f.key);
      return base;
    });

  const sel = (f: Facet) => (filters[f.key] as string[] | undefined) ?? [];

  // Toggle a value in/out of filters[key] (within-facet OR, across-facet AND —
  // matches the service's _filter). Empty → drop the key.
  const toggleValue = (key: string, value: string) => {
    const current = (filters[key] as string[] | undefined) ?? [];
    const next = current.includes(value) ? current.filter((x) => x !== value) : [...current, value];
    setFilter(key, next.length ? next : null);
  };
  const clearAll = () => Object.keys(filters).forEach((k) => setFilter(k, null));

  // Active chips — filters × facets, resolved to readable value labels.
  const chips: ActiveChip[] = facets.flatMap((f) =>
    sel(f).map((v) => ({
      key: f.key,
      value: v,
      label: f.values?.find((x) => x.value === v)?.label ?? v,
    })),
  );

  useCardExport(
    useMemo(() => {
      const status: 'loading' | 'loaded' | 'error' | 'idle' = !enabled
        ? 'idle'
        : isLoading && !res
          ? 'loading'
          : error
            ? 'error'
            : res
              ? 'loaded'
              : 'idle';
      return {
        label: 'Filter',
        content_key: 'product_facets',
        status,
        query_key: enabled ? ['strategy-data', strategyName, params] : null,
        text: res ? `${facets.length} facets` : 'Filter',
        json: res ? { active: Object.keys(filters), facets: facets.map((f) => `${f.key}:${f.tier}`) } : null,
      };
    }, [enabled, isLoading, error, res, strategyName, params, filters, facets]),
  );

  const facetCard = (f: Facet) => (
    <CardPlacementProvider key={f.key} value={{ surface: 'canvas', slot_id: `product_facet:${f.key}` }}>
      <FacetCard
        facet={f}
        selected={sel(f)}
        open={isOpen(f)}
        onToggleOpen={() => toggleOpen(f)}
        onToggle={(v) => toggleValue(f.key, v)}
        moreLabel={moreLabel}
      />
    </CardPlacementProvider>
  );

  return (
    <div className="p-2 space-y-2">
      <div className="flex items-center gap-2 px-1 pb-1">
        <SlidersHorizontal className="h-3.5 w-3.5 text-muted-foreground" />
        <span className={text.heading}>{labelTitle}</span>
      </div>

      {!enabled && <div className={cn(text.muted, 'px-1')}>{labelEmpty}</div>}

      {enabled && isLoading && !res && (
        <div className={cn(text.muted, 'flex items-center gap-2 px-1')}>
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          {labelLoading}
        </div>
      )}

      {enabled && error && <div className={cn(text.muted, 'px-1')}>Failed: {(error as Error).message}</div>}

      {res && (
        <>
          <ActiveFiltersCard chips={chips} onRemove={toggleValue} onClearAll={clearAll} clearAllLabel={clearAllLabel} />

          <div className="space-y-2">
            {spine.map(facetCard)}
            {tail.length > 0 && (
              <div className="pt-1">
                <div className={cn(text.muted, 'px-1 pt-1 text-[11px]')}>{labelTail}</div>
                <div className="space-y-2">{tail.map(facetCard)}</div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
